<template>
    <v-container style="height: 100%">
        <v-overlay v-model="loading">
            <FullscreenLoader />
        </v-overlay>
        <v-row v-if="!loading">
            <v-spacer />
            <v-col :cols="mainCols" style="min-width: 600px">
                <div
                    class="text-left text-display-large text-typography1"
                    style="padding-bottom: 20px"
                >
                    Calculation Browser
                </div>
                <div style="align-self: start">
                    <v-card>
                        <v-card-title
                            class="
                                text-typography1
                                font-weight-medium
                                text-title-large
                            "
                        >
                            {{ library.name }}
                            <v-btn
                                size="x-small"
                                disabled
                                v-if="
                                    items.length &&
                                    items[0].libraryVersionStatusId
                                "
                                variant="outlined"
                                class="text-body-small ml-2 pr-4 pl-4"
                            >
                                {{ getLibCategory }}
                            </v-btn>
                        </v-card-title>
                        <v-card-text
                            v-if="items.length && items[0].description"
                            class="text-typography1"
                        >
                            {{ items[0].description }}
                        </v-card-text>
                    </v-card>
                    <Treeview
                        v-if="items.length > 0 && !noCalcsError"
                        :items="items"
                        :firstOpen="open"
                        :url="true"
                        :libraryVersions="allVersions"
                        :firstShow="show"
                        :latest="latest"
                        :calcs="calcs"
                        class="ma-1"
                    />
                </div>
            </v-col>
            <v-spacer />
        </v-row>
    </v-container>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { useDisplay } from 'vuetify'
import helpers from '../../helpers'
import Treeview from '../treeview/Treeview.vue'
import FullscreenLoader from '../shared/FullscreenLoader.vue'

const store = useStore()
const route = useRoute()
const { name: displayName } = useDisplay()

const library = ref({})
const allVersions = ref([])
const items = ref([])
const loading = ref(false)
const open = ref([])
const show = ref({})
const latest = ref(false)
const calcs = ref([])
const noCalcsError = ref(false)

const libCategories = computed(() => store.state.libCategories)

const getLibCategory = computed(() =>
    items.value.length
        ? libCategories.value[items.value[0].libraryVersionStatusId]
        : ''
)

const mainCols = computed(() => {
    switch (displayName.value) {
        case 'xs':
            return 12
        case 'sm':
            return 12
        case 'md':
            return 12
        case 'lg':
            return 10
        default:
            return 8
    }
})

function setOpen(versions) {
    if (route.query.path) {
        const openArr = []
        let parts = route.query.path.split('-')
        openArr.push(versions[0].id)
        if (parts.length > 1 && parts[1].includes('beta')) {
            parts = parts.splice(1, 1)
        }
        let path = ''
        for (let i = 1; i < parts.length; i++) {
            let libraryName = library.value.name
            if (libraryName.includes(' '))
                libraryName = libraryName.replaceAll(' ', '')
            path += '/' + parts[i]
            const id =
                `n-v${versions[0].libraryVersionId}-${libraryName}${path}`.toLowerCase()
            openArr.push(id)
        }
        open.value = openArr
    }
}

function showRightVersion(allVers) {
    if (route.query.path) {
        let parts = route.query.path.split('-')
        let version = parts[0]
        const latestVersion = [allVers.find((v) => !v.isPrerelease)]
        if (version === 'latest') {
            latest.value = true
            return latestVersion
        } else if (version.charAt(0) === 'v') {
            version = version.substring(1)
        }
        if (parts.length > 1 && parts[1].includes('beta')) {
            version += `-${parts[1]}`
        }
        const selectedVersion = [allVers.find((v) => v.version === version)]
        latest.value = latestVersion[0]?.version === selectedVersion[0]?.version
        return selectedVersion
    }
    return allVers
}

async function loadChildren(vers) {
    const result = await store.dispatch('getVersionCalc', vers.libraryVersionId)
    if (result && result.length > 0) {
        calcs.value = result
        noCalcsError.value = false
        vers.children = helpers.buildTree(result)
        helpers.addCount(vers)
        const showUpdates = {}
        result.forEach((calc) => {
            showUpdates[calc.calcId] = false
        })
        show.value = { ...show.value, ...showUpdates }
        vers.children.forEach((node) => helpers.addCount(node))
    } else {
        noCalcsError.value = true
        store.commit('SET_ALERT_ERRORS', [
            `The library version you're trying to use has no calcs. Please either check with the library owner, or ensure the library was uploaded in the right format.`,
        ])
    }
}

async function updatePage(libraryId) {
    loading.value = true
    try {
        library.value = await store.dispatch('getLibrary', libraryId)
        if (!library.value) {
            store.commit('SET_ALERT_ERRORS', [
                `Library not found. It may have been deleted or you may not have access.`,
            ])
            console.error('LibraryBrowser updatePage error: library not found')
            return
        }
        const versions = await store.dispatch('getLibraryVersions', libraryId)
        if (!versions || versions.length === 0) {
            store.commit('SET_ALERT_ERRORS', [
                `No versions found for this library.`,
            ])
            console.error('LibraryBrowser updatePage error: no versions found')
            return
        }
        const chosenVersions = showRightVersion(versions)
        if (chosenVersions.length === 1 && chosenVersions[0] === undefined) {
            allVersions.value = []
            store.commit('SET_ALERT_ERRORS', [
                `The library version you're trying to use has either moved or been deleted. <a href="/docs/explanation/404.html" target="_blank">Refer to the documentation site for next steps.</a>`,
            ])
            return
        }
        const newItems = []
        chosenVersions.forEach((vers) => {
            vers.id = `v${vers.version}`
            vers.name = `v${vers.version}`
            vers.children = []
            vers.chip = vers.isPrerelease ? vers.suffix : 'stable'
            newItems.push(vers)
        })
        const stableChosen = chosenVersions.find((v) => !v.isPrerelease)
        const stableAll = versions.find((v) => !v.isPrerelease)
        if (
            stableChosen &&
            stableAll &&
            stableAll.version === stableChosen.version
        ) {
            stableChosen.chip = 'latest stable'
        }
        // Load children before setting items to avoid a double render
        if (chosenVersions.length === 1) {
            await loadChildren(chosenVersions[0])
        }
        items.value = newItems
        setOpen(chosenVersions)
        if (chosenVersions[0].publishLocation !== 'Kubernetes') {
            store.dispatch(
                'warmLibraryVersionByLibVerId',
                chosenVersions[0].libraryVersionId
            )
        }
        allVersions.value = versions
    } catch (err) {
        console.error('LibraryBrowser updatePage error:', err)
        store.commit('SET_ALERT_ERRORS', [
            `Something went wrong loading the library. Please try refreshing the page.`,
        ])
    } finally {
        loading.value = false
    }
}

// Only re-run when the libraryId param actually changes
watch(
    () => route.params.libraryId,
    async (newId, oldId) => {
        if (route.name === 'libraryBrowser' && newId !== oldId) {
            await updatePage(newId)
        }
    }
)

onMounted(async () => {
    await updatePage(route.params.libraryId)
})
</script>
